import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../api";

/**
 * MCP servers available in the active environment (admin globals + servers
 * added to this environment), annotated with the caller's per-server
 * connection state and per-(environment, server) enable toggle.
 *
 * Connecting/enabling affects NEW chats only (MCP servers bind to the agent at
 * thread creation), consistent with the "require approval" setting.
 */
export function useMcpServers(envId, { threadIdRef, onRebound } = {}) {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Returns the fetched list as well as storing it — connect() compares the
  // before/after snapshots to decide whether an OAuth attempt landed.
  const refresh = useCallback(async () => {
    if (!envId) { setServers([]); return []; }
    setLoading(true);
    try {
      const list = await api.listEnvMcpServers(envId);
      setServers(list);
      setError(null);
      return list;
    } catch (e) {
      setError(e);
      return [];
    } finally {
      setLoading(false);
    }
  }, [envId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Server icons are resolved in a background thread on add/connect, so an
  // immediate refresh may land before the icon is cached. Re-poll the list a
  // couple of times so the resolved icon appears on its own. Timers are
  // cancelled on env change / unmount so a stale-env refresh can't fire.
  const iconTimersRef = useRef([]);
  const scheduleIconRefresh = useCallback(() => {
    [1500, 4000].forEach((ms) => {
      iconTimersRef.current.push(setTimeout(() => refresh(), ms));
    });
  }, [refresh]);
  useEffect(() => () => {
    iconTimersRef.current.forEach(clearTimeout);
    iconTimersRef.current = [];
  }, [refresh]);

  const connect = useCallback(async (serverUrl) => {
    // Open the popup BEFORE the initiate fetch — popup blockers only allow
    // window.open while the click's user-gesture is still live. The blank
    // window is then navigated to the provider's sign-in page.
    const popup = api.openOauthPopup();
    let oauthUrl;
    try {
      // Pass the open chat so the callback can attach the new server to it.
      ({ oauth_url: oauthUrl } = await api.initiateMcpOauth(
        serverUrl, envId, threadIdRef?.current));
    } catch (e) {
      try { popup && popup.close(); } catch (_) {}
      throw e;
    }
    // Snapshot before the popup so we can tell a connection that just landed
    // from one that was already there (a cancelled RE-auth leaves `connected`
    // true, so connected-ness alone can't answer it).
    const before = servers.find((s) => s.url === serverUrl);
    const result = await api.runMcpOauthPopup(oauthUrl, serverUrl, popup);
    const after = (await refresh()).find((s) => s.url === serverUrl);
    scheduleIconRefresh();

    // The popup vanished without posting back. The server is the authority on
    // whether that was a success we simply didn't hear about — a connection
    // that is live AND whose own last_connected has moved since our snapshot.
    // Both timestamps come from the server, so no client/server clock skew
    // enters into it.
    if (result?.closed) {
      const landed = after?.connected && !after.needs_reauth
        && after.last_connected !== before?.last_connected;
      if (!landed) throw new Error("Authentication was cancelled.");
      // The callback may have rebound the open chat before the window closed,
      // and we lost that flag with the message. Reconnecting is cheap.
      onRebound?.();
      return;
    }
    // The server rebound the open chat to a new session — reconnect its stream.
    if (result?.rebound) onRebound?.();
  }, [envId, servers, refresh, scheduleIconRefresh, threadIdRef, onRebound]);

  const reauthenticate = connect;

  const disconnect = useCallback(async (serverUrl) => {
    await api.disconnectMcp(serverUrl);
    await refresh();
  }, [refresh]);

  const setEnabled = useCallback(async (serverUrl, enabled) => {
    // Optimistic flip; roll back on error.
    setServers((prev) => prev.map((s) => s.url === serverUrl ? { ...s, enabled } : s));
    try {
      await api.setMcpEnabled(serverUrl, envId, enabled);
    } catch (e) {
      setServers((prev) => prev.map((s) => s.url === serverUrl ? { ...s, enabled: !enabled } : s));
      throw e;
    }
  }, [envId]);

  const connectedCount = servers.filter((s) => s.connected).length;
  const needsReauthCount = servers.filter((s) => s.connected && s.needs_reauth).length;

  return {
    servers, connectedCount, needsReauthCount, loading, error, refresh,
    connect, reauthenticate, disconnect, setEnabled,
  };
}
